import { APP_INITIALIZER, Injectable, LOCALE_ID, NgModule, Provider } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, RouterStateSnapshot } from '@angular/router';
import { CultureInterceptor } from './culture.interceptor';
import { LocalizeLinkPipe, TranslateAroundPipe, TranslateCategoryPipe, TranslateHtmlPipe, TranslatePipe } from './culture.pipes';
import { CultureService, initCulture, localeIdFor } from './culture.service';
import { usesSellerSet } from './culture.config';
import { CultureSuggestComponent, CultureSwitchComponent } from './culture-switch.component';

/**
 * On the account area's route (canActivateChild, so it runs for every page in it): the dashboard's labels (core, the host's,
 * seller) loaded before a dashboard page draws, when the visitor came in from the storefront or from a shopper page (the
 * cart, checkout and sign-in use the storefront's: usesSellerSet). At most 3 seconds; the page draws either way
 * (Multilang_Culture_Spec.md 5.1).
 */
@Injectable({ providedIn: 'root' })
export class CultureSellerGuard implements CanActivate, CanActivateChild {
    constructor(private culture: CultureService) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.load(state && state.url);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.load(state && state.url);
    }

    private load(url: string): Promise<boolean> {
        return usesSellerSet(url || '', this.culture.languages) ? this.culture.loadSellerSet() : Promise.resolve(true);
    }
}

/**
 * The app-wide part, for the root module's providers: the page's labels loaded before the first draw, LOCALE_ID from the
 * page's language, and the page's language on every API call.
 */
export const CULTURE_PROVIDERS: Provider[] = [
    { provide: APP_INITIALIZER, useFactory: initCulture, deps: [CultureService], multi: true },
    { provide: LOCALE_ID, useFactory: localeIdFor, deps: [CultureService] },
    { provide: HTTP_INTERCEPTORS, useClass: CultureInterceptor, multi: true },
];

/** The pipes and the two components, for any module whose templates show labels or app links. */
@NgModule({
    imports: [CommonModule],
    declarations: [TranslatePipe, TranslateHtmlPipe, TranslateAroundPipe, TranslateCategoryPipe, LocalizeLinkPipe, CultureSwitchComponent, CultureSuggestComponent],
    exports: [TranslatePipe, TranslateHtmlPipe, TranslateAroundPipe, TranslateCategoryPipe, LocalizeLinkPipe, CultureSwitchComponent, CultureSuggestComponent],
})
export class CultureModule { }
